/**
 * @file OrderCardHeader.tsx
 * @description Шапка карточки заказа в верхней панели кабинета (phase19.24
 * этап 1, шаг 5.3; phase19.31 — `CabinetPageHeader`, SC34, SC52, SC67).
 *
 * В панели: «‹ к реестру», номер заказа, стадия пилюлей (тон по виду
 * стадии, цвет магазина — точкой), одно главное действие и рядом — видимые
 * кнопки остальных действий, «Отменить заказ» последней в опасном тоне. Меню
 * «⋯» у карточки нет (SC67). На телефоне и планшете кнопки переносятся строкой
 * под шапку (`secondaryActions` — тот же приём, что у карточки сделки), главное
 * действие уходит в общую нижнюю полосу каркаса, а подсказка «что дальше» при
 * чужом ходе — над ней.
 *
 * Пока карточка тихо перечитывается, рядом со стадией крутится небольшой
 * значок — страница при этом не пересобирается.
 */

import { Loader2 } from "lucide-react";

import { orderHeaderActions } from "./order-menu-actions";
import { OrderPrimaryAction } from "./OrderPrimaryAction";
import { OrderStagePill } from "./OrderStagePill";
import { PageActionButton } from "../ui/cabinet-page-header-parts";
import { CabinetPageHeader } from "../ui/CabinetPageHeader";
import { ORDER_SOURCE_LABEL, type OrderDto } from "../../lib/api-orders";
import type { OrderActionKey } from "../../lib/order-actions";
import type { OrderNowModel, ResolvedPrimaryAction } from "../../lib/order-now";
import { buildDeliveryLine } from "../../lib/order-summary-lines";

interface Props {
  order: OrderDto;
  slug: string | undefined;
  now: OrderNowModel | null;
  primary: ResolvedPrimaryAction | null;
  canManage: boolean;
  acting: boolean;
  refreshing: boolean;
  onAction: (action: OrderActionKey) => void;
  onCancel: () => void;
}

/** Пояснение под номером: клиент, источник, доставка, условия оплаты. */
function orderSubtitle(order: OrderDto): string {
  return [
    order.customerDisplayName,
    ORDER_SOURCE_LABEL[order.source],
    buildDeliveryLine(order)?.text,
    order.paymentTerms === "POSTPAY" ? "оплата при получении" : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function OrderCardHeader({ order, slug, now, primary, canManage, acting, refreshing, onAction, onCancel }: Props) {
  const danger = now?.tone === "danger";
  const hint = now && !now.isManagerTurn ? now.nextStepHint : null;
  const actions = orderHeaderActions({ order, now, primary, canManage, acting, onAction, onCancel });

  return (
    <CabinetPageHeader
      title={order.number}
      subtitle={orderSubtitle(order)}
      back={{ to: `/shop/${slug}/orders`, label: "К реестру заказов" }}
      status={
        <span className="inline-flex items-center gap-2">
          <OrderStagePill order={order} />
          {refreshing ? (
            <Loader2 role="status" aria-label="Заказ обновляется" className="size-4 animate-spin text-on-surface-variant" />
          ) : null}
        </span>
      }
      secondaryActions={
        actions.length > 0 ? actions.map((action) => <PageActionButton key={action.key} action={action} />) : undefined
      }
      mobileNote={hint ?? undefined}
      action={
        primary ? (
          <OrderPrimaryAction
            primary={primary}
            filled={!!now?.isManagerTurn && !danger}
            danger={danger}
            disabled={acting}
            onAction={onAction}
          />
        ) : undefined
      }
    />
  );
}
